import { assetService } from "../../../shared/services/AssetService";
import { AssetType } from "../../../shared/types/AssetType";
import { mathCalculationService } from "../../../shared/services/MathCalculationService";
import { OrderState } from "../../../shared/types/OrderState";
import { PortfolioGroupedItem } from "../models/PortfolioGroupedItem";
import { PositionModel } from "../../portfolio-info/types/PositionModel";
import { OrderModel } from "../../portfolio-info/types/OrderModel";
import { PortfolioModel } from "../../portfolio-info/types/PortfolioModel";
import { TargetPlatform } from "../../../shared/types/TargetPlatform";
import {
    PortfolioBalanceValuationContext,
    portfolioBalanceCalculationService,
} from "../../../shared/services/portfolio-balance-calculation-service/PortfolioBalanceCalculationService";

export type PositionsValuationContext = PortfolioBalanceValuationContext;

class PositionsProcessor {
    getSummarizedOrders = (
        selectedPortfolio: PortfolioModel | null,
        mergePositionsAcrossPlatforms: boolean = false,
        targetPlatformFilter: TargetPlatform | null = null,
        valuationContext: PositionsValuationContext | null = null,
    ): PortfolioGroupedItem[] => {
        if (selectedPortfolio === null)
            return [];

        const getAverageOpenPrice = (groupedItemModel: PortfolioGroupedItem): number | null => {
            if (assetService.isGroupedOptions(groupedItemModel) &&
                assetService.isGroupedOptionsExpired(groupedItemModel)
            ) {
                return null;
            }

            if (groupedItemModel.currentSize === 0)
                return null;

            if (groupedItemModel.previousSize === 0)
                return groupedItemModel.processingItemOpenPrice;

            const isPreviousPositionBullish = Math.sign(groupedItemModel.previousSize) === 1;
            const isCurrentPositionBullish = Math.sign(groupedItemModel.currentSize) === 1;

            if (isCurrentPositionBullish !== isPreviousPositionBullish)
                return groupedItemModel.processingItemOpenPrice;

            if (isCurrentPositionBullish === isPreviousPositionBullish) {
                const sizeChange = groupedItemModel.currentSize - groupedItemModel.previousSize;

                if ((isCurrentPositionBullish && sizeChange < 0) || (!isCurrentPositionBullish && sizeChange > 0))
                    return groupedItemModel.averageOpenPrice;

                const groupPositionWeightedPrice = groupedItemModel.averageOpenPrice! * groupedItemModel.previousSize;
                const processingPositionWeightedPrice = groupedItemModel.processingItemOpenPrice * sizeChange;
                const totalSize = groupedItemModel.previousSize + sizeChange;

                return (groupPositionWeightedPrice + processingPositionWeightedPrice) / totalSize;
            }

            throw new Error("Unhandled case during processing getAverageOpenPrice()");
        }

        const isOpenPosition = (position: PositionModel): boolean =>
            mathCalculationService.round(position.size, 6) !== 0 &&
            !(assetService.isPositionOption(position) &&
                assetService.isPositionOptionExpired(position));

        const addPortfolioAllocation = (
            groupedItem: PortfolioGroupedItem,
            portfolioId: number | null,
            currentSize: number,
        ): void => {
            if (portfolioId === null) {
                return;
            }

            const allocation = groupedItem.portfolioAllocations.find(x => x.portfolioId === portfolioId);
            if (allocation) {
                allocation.currentSize = mathCalculationService.round(
                    allocation.currentSize + currentSize,
                    6,
                );
                return;
            }

            groupedItem.portfolioAllocations.push({ portfolioId, currentSize });
        };

        const mapPositions = (positions: PositionModel[]): PortfolioGroupedItem[] => {
            const result: PortfolioGroupedItem[] = [];

            positions.forEach(position => {
                const valuation = portfolioBalanceCalculationService.getPositionValuation(
                    position,
                    valuationContext,
                );
                const groupedItemModel = result.find(x =>
                    x.symbol === position.symbol &&
                    x.type === position.type &&
                    (mergePositionsAcrossPlatforms || x.targetPlatform === position.targetPlatform));

                if (groupedItemModel) {
                    addPortfolioAllocation(groupedItemModel, position.portfolioId, position.size);
                    groupedItemModel.previousSize = mathCalculationService.round(groupedItemModel.currentSize, 6);
                    groupedItemModel.processingItemOpenPrice = position.averageOpenPrice;
                    groupedItemModel.currentSize = mathCalculationService.round(groupedItemModel.currentSize + position.size, 6);
                    groupedItemModel.fee = Math.round((groupedItemModel.fee + (position.fee ?? 0)) * 100) / 100;
                    groupedItemModel.volume += position.volume;
                    groupedItemModel.balance += portfolioBalanceCalculationService
                        .calculatePositionBalance(position, valuation);
                    groupedItemModel.delta += valuation.delta;

                    if (groupedItemModel.gamma !== null)
                        groupedItemModel.gamma += valuation.gamma ?? 0;

                    if (groupedItemModel.vega !== null)
                        groupedItemModel.vega += valuation.vega ?? 0;

                    if (groupedItemModel.theta !== null)
                        groupedItemModel.theta += valuation.theta ?? 0;

                    groupedItemModel.averageOpenPrice = getAverageOpenPrice(groupedItemModel);
                } else {
                    result.push({
                        symbol: position.symbol,
                        portfolioAllocations: [{
                            portfolioId: position.portfolioId,
                            currentSize: position.size,
                        }],
                        type: position.type,
                        targetPlatform: position.targetPlatform,
                        expireAt: position.expireAt,
                        expirationPrice: position.expirationPrice,
                        strike: position.strike,
                        previousSize: 0,
                        currentSize: position.size,
                        fee: position.fee ?? 0,
                        volume: position.volume,
                        processingItemOpenPrice: position.averageOpenPrice,
                        averageOpenPrice: position.averageOpenPrice,
                        markPrice: valuation.markPrice,
                        balance: portfolioBalanceCalculationService.calculatePositionBalance(
                            position,
                            valuation,
                        ),
                        impliedVolatility: position.impliedVolatility,
                        customImpliedVolatility: position.customImpliedVolatility ?? position.impliedVolatility,
                        customImpliedVolatilityStep: position.customImpliedVolatilityStep,
                        delta: valuation.delta,
                        gamma: valuation.gamma,
                        vega: valuation.vega,
                        theta: valuation.theta,
                        platformAllocations: [],
                    });
                }
            });

            return result;
        }

        const result: PortfolioGroupedItem[] = mapPositions(selectedPortfolio.positions
            .filter(position =>
                (targetPlatformFilter === null ||
                    position.targetPlatform === targetPlatformFilter) &&
                (!mergePositionsAcrossPlatforms || isOpenPosition(position))));

        const getVolume = (orderModel: OrderModel): number =>
            -(orderModel.size * orderModel.openPrice);

        selectedPortfolio.orders
            .filter(order => order.state === OrderState.Active &&
                (targetPlatformFilter === null || order.targetPlatform === targetPlatformFilter) &&
                (!mergePositionsAcrossPlatforms ||
                    !(assetService.isOption(order) && assetService.isOptionExpired(order))))
            .sort((x, y) => x.openedPositionAt > y.openedPositionAt ? 1 : -1)
            .forEach(order => {
                const valuation = portfolioBalanceCalculationService.getOrderValuation(
                    order,
                    valuationContext,
                );
                let groupedItemModel = result.find(x =>
                    x.symbol === order.symbol &&
                    x.type === order.type &&
                    (mergePositionsAcrossPlatforms || x.targetPlatform === order.targetPlatform));
                
                if (groupedItemModel) {
                    groupedItemModel.previousSize = mathCalculationService.round(groupedItemModel.currentSize, 6);
                    groupedItemModel.processingItemOpenPrice = order.openPrice;

                    groupedItemModel.fee = Math.round((groupedItemModel.fee + (order.fee ?? 0)) * 100) / 100;
                    groupedItemModel.volume += getVolume(order);
                    groupedItemModel.balance += portfolioBalanceCalculationService
                        .calculateOrderBalance(order, valuation);

                    if (!(assetService.isOption(order) && assetService.isOptionExpired(order))) {
                        addPortfolioAllocation(groupedItemModel, order.portfolioId, order.size);
                        groupedItemModel.currentSize = mathCalculationService
                            .round(groupedItemModel.currentSize + order.size, 6);

                        groupedItemModel.delta += valuation.delta;

                        if (groupedItemModel.gamma !== null)
                            groupedItemModel.gamma += valuation.gamma ?? 0;

                        if (groupedItemModel.vega !== null)
                            groupedItemModel.vega += valuation.vega ?? 0;

                        if (groupedItemModel.theta !== null)
                            groupedItemModel.theta += valuation.theta ?? 0;
                    }

                    groupedItemModel.averageOpenPrice = getAverageOpenPrice(groupedItemModel);
                } else {
                    groupedItemModel =
                        {
                            symbol: order.symbol,
                            portfolioAllocations: order.portfolioId !== null
                                ? [{ portfolioId: order.portfolioId, currentSize: order.size }]
                                : [],
                            type: order.type,
                            targetPlatform: order.targetPlatform,
                            expireAt: order.expireAt,
                            expirationPrice: order.expirationPrice,
                            strike: order.strike,
                            previousSize: 0,
                            currentSize: mathCalculationService.round(order.size, 6),
                            fee: order.fee ?? 0,
                            volume: getVolume(order),
                            processingItemOpenPrice: order.openPrice,
                            averageOpenPrice: order.openPrice,
                            markPrice: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? 0
                                : valuation.markPrice,
                            balance: portfolioBalanceCalculationService.calculateOrderBalance(
                                order,
                                valuation,
                            ),
                            impliedVolatility: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.markImpliedVolatility,
                            customImpliedVolatility: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.customImpliedVolatility ?? order.markImpliedVolatility,
                            customImpliedVolatilityStep: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : order.customImpliedVolatilityStep,
                            delta: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? 0
                                : valuation.delta,
                            gamma: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : valuation.gamma,
                            vega: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : valuation.vega,
                            theta: assetService.isOption(order) && assetService.isOptionExpired(order)
                                ? null
                                : valuation.theta,
                            platformAllocations: [],
                        };

                    result.push(groupedItemModel);
                }
            });

        result
            .sort((x, y) => {
                if ((x.type === AssetType.Futures ||
                        x.type === AssetType.Spot) &&
                    (y.type === AssetType.Call ||
                        y.type === AssetType.Put))
                {
                    return -1;
                }

                if (x.type === AssetType.Spot && y.type === AssetType.Futures)
                {
                    return -1;
                }

                if (x.type === AssetType.Call && y.type === AssetType.Put)
                {
                    return -1;
                }

                return 1;
            })
            .sort((x, y) => {
                if (!x.expireAt || !y.expireAt)
                {
                    return 0;
                }

                if (x.type !== AssetType.Call && y.type !== AssetType.Put)
                {
                    return 0;
                }

                return x.expireAt > y.expireAt ? 1 : -1;
            })
            .sort((x, y) => {
                if (!x.strike || !y.strike)
                {
                    return 0;
                }

                if (x.type !== AssetType.Call && y.type !== AssetType.Put)
                {
                    return 0;
                }

                return x.strike > y.strike ? 1 : -1;
            });

        const positionsByPlatform = mergePositionsAcrossPlatforms
            ? this.getSummarizedOrders(
                selectedPortfolio,
                false,
                targetPlatformFilter,
                valuationContext,
            )
            : result;

        result.forEach(position => {
            if (selectedPortfolio.portfolioSettings?.includeFeesInBalance) {
                position.balance += position.fee;
            }
            position.platformAllocations = positionsByPlatform
                .filter(platformPosition =>
                    platformPosition.symbol === position.symbol &&
                    platformPosition.type === position.type &&
                    (!mergePositionsAcrossPlatforms ||
                        (mathCalculationService.round(platformPosition.currentSize, 6) !== 0 &&
                            !(assetService.isGroupedOptions(platformPosition) &&
                                assetService.isGroupedOptionsExpired(platformPosition)))) &&
                    (mergePositionsAcrossPlatforms ||
                        platformPosition.targetPlatform === position.targetPlatform))
                .map(platformPosition => ({
                    targetPlatform: platformPosition.targetPlatform,
                    currentSize: platformPosition.currentSize,
                }))
                .sort((x, y) => x.targetPlatform - y.targetPlatform);
        });

        return mergePositionsAcrossPlatforms
            ? result.filter(position =>
                mathCalculationService.round(position.currentSize, 6) !== 0)
            : result;
    }
}

export const positionsProcessor = new PositionsProcessor();
